import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import L from "leaflet";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import type { NearbyBusiness } from "@dropin/shared/schemas";
import {
  addFavorite,
  claimPerk,
  listFavorites,
  removeFavorite,
  searchNearby,
} from "../api";
import { CityAutocomplete } from "./CityAutocomplete";
import { useIsMobile } from "../hooks/useMediaQuery";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";

type Coords = { lat: number; lon: number };
type LocationStatus =
  | { kind: "idle" }
  | { kind: "requesting" }
  | { kind: "have-coords"; source: "device" | "manual"; coords: Coords; label?: string }
  | { kind: "manual"; reason: "denied" | "unsupported" | "error" };

const DEFAULT_RADIUS_KM = 25;

// Custom marker so we don't fight Vite over Leaflet's default PNG paths.
// Pin uses the brand primary colour with a subtle drop shadow.
const businessPin = L.divIcon({
  className: "dropin-pin",
  html: `
    <svg viewBox="0 0 24 28" width="28" height="32" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 16 12 16s12-7 12-16C24 5.4 18.6 0 12 0z"
            fill="#14B8A6" stroke="white" stroke-width="2"/>
      <circle cx="12" cy="11" r="4.5" fill="white"/>
    </svg>`,
  iconSize: [28, 32],
  iconAnchor: [14, 32],
  popupAnchor: [0, -28],
});

const youPin = L.divIcon({
  className: "dropin-you-pin",
  html: `
    <span style="display:block;width:14px;height:14px;border-radius:50%;background:#1e293b;border:3px solid white;box-shadow:0 0 0 2px #1e293b;"></span>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

export function BrowseNearby() {
  const isMobile = useIsMobile();
  const [location, setLocation] = useState<LocationStatus>({ kind: "idle" });
  const [results, setResults] = useState<NearbyBusiness[] | null>(null);
  const [favBiz, setFavBiz] = useState<Set<string>>(new Set());
  const [favPerk, setFavPerk] = useState<Set<string>>(new Set());
  const [loadingResults, setLoadingResults] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load favorites once
  useEffect(() => {
    listFavorites()
      .then(({ businessIds, perkIds }) => {
        setFavBiz(new Set(businessIds));
        setFavPerk(new Set(perkIds));
      })
      .catch(() => {});
  }, []);

  // Kick off geolocation on mount
  useEffect(() => {
    if (location.kind !== "idle") return;
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setLocation({ kind: "manual", reason: "unsupported" });
      return;
    }
    setLocation({ kind: "requesting" });
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        setLocation({
          kind: "have-coords",
          source: "device",
          coords: { lat: pos.coords.latitude, lon: pos.coords.longitude },
        }),
      (err) =>
        setLocation({
          kind: "manual",
          reason: err.code === err.PERMISSION_DENIED ? "denied" : "error",
        }),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60_000 },
    );
  }, [location.kind]);

  // Fetch nearby whenever coords change
  useEffect(() => {
    if (location.kind !== "have-coords") return;
    let cancelled = false;
    setLoadingResults(true);
    setError(null);
    searchNearby({
      lat: location.coords.lat,
      lon: location.coords.lon,
      radiusKm: DEFAULT_RADIUS_KM,
    })
      .then(({ businesses }) => {
        if (!cancelled) setResults(businesses);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "search failed");
      })
      .finally(() => {
        if (!cancelled) setLoadingResults(false);
      });
    return () => {
      cancelled = true;
    };
  }, [location]);

  async function toggleFavorite(kind: "business" | "perk", id: string) {
    const set = kind === "business" ? favBiz : favPerk;
    const setter = kind === "business" ? setFavBiz : setFavPerk;
    const next = new Set(set);
    const wasFav = next.has(id);
    if (wasFav) next.delete(id);
    else next.add(id);
    setter(next); // optimistic
    try {
      if (wasFav) await removeFavorite(kind, id);
      else await addFavorite(kind, id);
    } catch {
      // revert on failure
      setter(set);
    }
  }

  function useManualLocation(coords: Coords, label: string) {
    setLocation({ kind: "have-coords", source: "manual", coords, label });
  }

  // Mobile fullscreen mode — only when we actually have coords + a map to show.
  if (isMobile && location.kind === "have-coords") {
    return (
      <MobileBrowse
        you={location.coords}
        sourceLabel={location.source === "device" ? "Near you" : "Near"}
        label={location.label}
        businesses={results ?? []}
        loading={loadingResults}
        favBiz={favBiz}
        favPerk={favPerk}
        onToggleFavorite={toggleFavorite}
        onPickLocation={useManualLocation}
        error={error}
      />
    );
  }

  return (
    <div className="grid gap-4">
      <LocationBar location={location} onReset={() => setLocation({ kind: "idle" })} />

      {location.kind === "manual" && (
        <ManualLocationPrompt reason={location.reason} onPick={useManualLocation} />
      )}

      {location.kind === "have-coords" && (
        <>
          <MapPanel
            you={location.coords}
            businesses={results ?? []}
            loading={loadingResults}
          />
          {error && <div className="card text-sm text-rose">{error}</div>}
          <ResultsList
            businesses={results ?? []}
            loading={loadingResults}
            favBiz={favBiz}
            favPerk={favPerk}
            onToggleFavorite={toggleFavorite}
          />
        </>
      )}

      {(location.kind === "idle" || location.kind === "requesting") && (
        <div className="card text-muted">Asking your browser for your location…</div>
      )}
    </div>
  );
}

function MobileBrowse({
  you,
  sourceLabel,
  label,
  businesses,
  loading,
  favBiz,
  favPerk,
  onToggleFavorite,
  onPickLocation,
  error,
}: {
  you: Coords;
  sourceLabel: string;
  label?: string;
  businesses: NearbyBusiness[];
  loading: boolean;
  favBiz: Set<string>;
  favPerk: Set<string>;
  onToggleFavorite: (kind: "business" | "perk", id: string) => void;
  onPickLocation: (coords: Coords, label: string) => void;
  error: string | null;
}) {
  const [sheetState, setSheetState] = useState<"peek" | "expanded">("peek");
  const [pickerOpen, setPickerOpen] = useState(false);
  const mapRef = useRef<L.Map | null>(null);

  // Prevent body scroll while the fullscreen map is mounted — feels native.
  useBodyScrollLock(true);

  // Peek height (just count + handle) — keep this in sync with the inline
  // class below so the recenter button can clear the sheet cleanly.
  const PEEK_HEIGHT = 96;

  const subtitle = label
    ? label
    : `${you.lat.toFixed(3)}, ${you.lon.toFixed(3)}`;

  function recenter() {
    const m = mapRef.current;
    if (!m) return;
    m.flyTo([you.lat, you.lon], Math.max(m.getZoom(), 14), { duration: 0.6 });
  }

  return (
    <>
      {/* Fullscreen map — anchored below header + the (now compact) tabs row. */}
      <div className="map-fullbleed bg-paper">
        <MapContainer
          center={[you.lat, you.lon]}
          zoom={13}
          scrollWheelZoom
          zoomControl={false}
          ref={(m) => {
            mapRef.current = m ?? null;
          }}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <RecenterOnChange center={[you.lat, you.lon]} />
          <Marker position={[you.lat, you.lon]} icon={youPin}>
            <Popup>You</Popup>
          </Marker>
          {businesses.map((b) => (
            <Marker key={b.id} position={[b.lat, b.lon]} icon={businessPin}>
              <Popup>
                <div className="text-sm grid gap-1">
                  <div className="font-semibold">{b.name}</div>
                  {b.city && <div className="text-muted">{b.city}</div>}
                  <div className="text-muted">{b.distanceKm} km away</div>
                  {b.perks.length > 0 && (
                    <div className="text-xs">
                      {b.perks.length} active perk{b.perks.length === 1 ? "" : "s"}
                    </div>
                  )}
                  {b.briefPublished ? (
                    <Link
                      to={`/brief/${b.shortCode || b.id}`}
                      className="mt-1 text-xs font-semibold"
                      data-testid={`popup-brief-link-${b.id}`}
                    >
                      View brief →
                    </Link>
                  ) : (
                    <div className="mt-1 text-xs text-muted">No brief yet</div>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {/* Compact location pill — entire pill is the tap target to change. */}
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          data-testid="change-location"
          className="absolute left-3 right-3 top-3 z-[500] flex items-center gap-2 rounded-pill bg-surface/95 backdrop-blur border border-border shadow-md pl-3.5 pr-2.5 py-2 text-left select-none-tactile active:scale-[0.99] transition-transform"
        >
          <span className="shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-pill bg-primary/10 text-primary">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M12 21s7-7.58 7-12a7 7 0 1 0-14 0c0 4.42 7 12 7 12z" />
              <circle cx="12" cy="9" r="2.5" />
            </svg>
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-[10px] uppercase tracking-wider text-muted font-semibold leading-none">
              {sourceLabel}
            </span>
            <span className="mt-0.5 block text-sm font-semibold text-ink truncate leading-tight">
              {subtitle}
            </span>
          </span>
          <span className="shrink-0 text-muted">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </button>

        {/* Searching indicator */}
        {loading && (
          <div className="absolute top-[64px] left-1/2 -translate-x-1/2 z-[500] rounded-pill bg-surface/95 backdrop-blur border border-border shadow-md px-3 py-1 text-xs text-muted">
            Searching nearby…
          </div>
        )}

        {/* Recenter on me — floating just above the sheet's peek height. */}
        <button
          type="button"
          onClick={recenter}
          aria-label="Recenter on my location"
          data-testid="map-recenter"
          className="absolute right-3 z-[550] inline-flex h-11 w-11 items-center justify-center rounded-pill bg-surface/95 backdrop-blur border border-border shadow-md text-ink active:scale-[0.97] transition-transform"
          style={{ bottom: `calc(${PEEK_HEIGHT}px + env(safe-area-inset-bottom) + 12px)` }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <circle cx="12" cy="12" r="3" />
            <path d="M12 2v3" />
            <path d="M12 19v3" />
            <path d="M2 12h3" />
            <path d="M19 12h3" />
          </svg>
        </button>
      </div>

      {/* Bottom sheet */}
      <div
        className="fixed inset-x-0 bottom-0 z-[600] bg-surface border-t border-border rounded-t-xl shadow-lg pb-safe transition-[max-height] duration-300 ease-out"
        style={{
          maxHeight:
            sheetState === "expanded"
              ? "calc(100dvh - var(--header-h, 56px) - var(--tabs-h, 0px) - 80px)"
              : `${PEEK_HEIGHT}px`,
        }}
        data-testid="mobile-results-sheet"
        data-state={sheetState}
      >
        <button
          type="button"
          onClick={() =>
            setSheetState((s) => (s === "peek" ? "expanded" : "peek"))
          }
          className="block w-full pt-2 pb-1.5 select-none-tactile"
          aria-label={sheetState === "peek" ? "Show all results" : "Collapse results"}
        >
          <span className="mx-auto block h-1 w-10 rounded-pill bg-border-strong" />
        </button>
        <div className="px-4 pb-2 flex items-center justify-between gap-2">
          <p className="text-sm font-semibold text-ink truncate">
            {businesses.length === 0
              ? loading
                ? "Searching nearby…"
                : "Nothing within 25 km"
              : `${businesses.length} ${businesses.length === 1 ? "spot" : "spots"} nearby`}
          </p>
          {sheetState === "peek" && businesses.length > 0 && (
            <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wider text-muted">
              Pull up
            </span>
          )}
        </div>
        <div
          className="px-3 pb-3 pt-1 overflow-y-auto overscroll-contain"
          style={{
            maxHeight:
              sheetState === "expanded"
                ? "calc(100dvh - var(--header-h, 56px) - var(--tabs-h, 0px) - 80px - 56px)"
                : "0px",
          }}
        >
          {error && (
            <div className="mb-2 rounded-md border border-rose/40 bg-rose/[0.06] px-3 py-2 text-xs text-rose">
              {error}
            </div>
          )}
          {businesses.length === 0 && !loading ? (
            <p className="text-xs text-muted px-1 py-2">
              Try changing your location.
            </p>
          ) : (
            <div className="grid gap-3">
              {businesses.map((b) => (
                <BusinessCard
                  key={b.id}
                  biz={b}
                  favorited={favBiz.has(b.id)}
                  favoritedPerks={favPerk}
                  onToggleFav={onToggleFavorite}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {pickerOpen && (
        <LocationPickerSheet
          onClose={() => setPickerOpen(false)}
          onPick={(coords, label) => {
            onPickLocation(coords, label);
            setPickerOpen(false);
          }}
        />
      )}
    </>
  );
}

function LocationPickerSheet({
  onClose,
  onPick,
}: {
  onClose: () => void;
  onPick: (coords: Coords, label: string) => void;
}) {
  const [value, setValue] = useState("");
  return (
    <div className="fixed inset-0 z-[750]">
      <button
        type="button"
        aria-label="Close location picker"
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0 bg-ink/70 animate-fade-in"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Pick a location"
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[92%] max-w-md bg-surface border border-border rounded-xl shadow-lg p-4 animate-scale-in"
        style={{ backgroundColor: "#FFFFFF" }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted">
              Change location
            </p>
            <h2 className="mt-1 text-lg font-semibold tracking-tightish text-ink">
              Where are you?
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-pill text-muted hover:text-ink hover:bg-paper"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </button>
        </div>
        <div className="mt-3">
          <CityAutocomplete
            value={value}
            onChange={setValue}
            onSelect={(s) => onPick({ lat: s.lat, lon: s.lon }, s.label)}
            placeholder="Start typing a city…"
            testid="mobile-location-picker"
          />
        </div>
        <p className="mt-2 text-xs text-muted">
          Pick any city — we'll show perks nearby.
        </p>
      </div>
    </div>
  );
}

function LocationBar({
  location,
  onReset,
}: {
  location: LocationStatus;
  onReset: () => void;
}) {
  if (location.kind !== "have-coords") return null;
  return (
    <div className="card flex items-center justify-between gap-3 flex-wrap py-3">
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wider text-muted font-semibold">
          {location.source === "device" ? "Near you" : "Near"}
        </p>
        <p className="text-sm font-semibold text-ink truncate">
          {location.label
            ? location.label
            : `${location.coords.lat.toFixed(3)}, ${location.coords.lon.toFixed(3)}`}
        </p>
      </div>
      <button className="btn-ghost text-sm" onClick={onReset} data-testid="change-location">
        Change
      </button>
    </div>
  );
}

function ManualLocationPrompt({
  reason,
  onPick,
}: {
  reason: "denied" | "unsupported" | "error";
  onPick: (coords: { lat: number; lon: number }, label: string) => void;
}) {
  const [value, setValue] = useState("");
  const message =
    reason === "denied"
      ? "No problem — pick your location and we'll surface perks nearby."
      : reason === "unsupported"
        ? "Your browser doesn't support geolocation. Pick a city to start."
        : "Couldn't read your location. Pick a city to start.";
  return (
    <div className="card grid gap-3">
      <div>
        <p className="text-sm font-semibold text-ink">Where are you?</p>
        <p className="mt-0.5 text-xs text-muted">{message}</p>
      </div>
      <CityAutocomplete
        value={value}
        onChange={setValue}
        onSelect={(s) => onPick({ lat: s.lat, lon: s.lon }, s.label)}
        placeholder="Start typing a city…"
        testid="manual-location"
      />
    </div>
  );
}

function MapPanel({
  you,
  businesses,
  loading,
}: {
  you: Coords;
  businesses: NearbyBusiness[];
  loading: boolean;
}) {
  return (
    <div className="rounded-lg border border-border overflow-hidden bg-paper relative">
      <MapContainer
        center={[you.lat, you.lon]}
        zoom={13}
        scrollWheelZoom
        style={{ height: 320, width: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <RecenterOnChange center={[you.lat, you.lon]} />
        <Marker position={[you.lat, you.lon]} icon={youPin}>
          <Popup>You</Popup>
        </Marker>
        {businesses.map((b) => (
          <Marker key={b.id} position={[b.lat, b.lon]} icon={businessPin}>
            <Popup>
              <div className="text-sm grid gap-1">
                <div className="font-semibold">{b.name}</div>
                {b.city && <div className="text-muted">{b.city}</div>}
                <div className="text-muted">{b.distanceKm} km away</div>
                {b.perks.length > 0 && (
                  <div className="text-xs">
                    {b.perks.length} active perk{b.perks.length === 1 ? "" : "s"}
                  </div>
                )}
                {b.briefPublished ? (
                  <Link
                    to={`/brief/${b.shortCode || b.id}`}
                    className="mt-1 text-xs font-semibold"
                    data-testid={`popup-brief-link-${b.id}`}
                  >
                    View brief →
                  </Link>
                ) : (
                  <div className="mt-1 text-xs text-muted">No brief yet</div>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      {loading && (
        <div className="absolute top-2 right-2 rounded-md bg-surface/90 px-2 py-1 text-xs text-muted shadow-xs">
          Searching…
        </div>
      )}
    </div>
  );
}

// react-leaflet doesn't reactively update center; this small helper does.
function RecenterOnChange({ center }: { center: [number, number] }) {
  const map = useMap();
  const last = useRef<string>("");
  useEffect(() => {
    const key = `${center[0]},${center[1]}`;
    if (key === last.current) return;
    last.current = key;
    map.setView(center, map.getZoom());
  }, [center, map]);
  return null;
}

function ResultsList({
  businesses,
  loading,
  favBiz,
  favPerk,
  onToggleFavorite,
}: {
  businesses: NearbyBusiness[];
  loading: boolean;
  favBiz: Set<string>;
  favPerk: Set<string>;
  onToggleFavorite: (kind: "business" | "perk", id: string) => void;
}) {
  if (loading && businesses.length === 0) {
    return <div className="card text-muted">Loading…</div>;
  }
  if (!loading && businesses.length === 0) {
    return (
      <div className="card text-muted">
        No businesses within 25 km. Try changing your location or widening the radius later.
      </div>
    );
  }
  return (
    <div className="grid gap-3">
      {businesses.map((b) => (
        <BusinessCard
          key={b.id}
          biz={b}
          favorited={favBiz.has(b.id)}
          favoritedPerks={favPerk}
          onToggleFav={(kind, id) => onToggleFavorite(kind, id)}
        />
      ))}
    </div>
  );
}

function BusinessCard({
  biz,
  favorited,
  favoritedPerks,
  onToggleFav,
}: {
  biz: NearbyBusiness;
  favorited: boolean;
  favoritedPerks: Set<string>;
  onToggleFav: (kind: "business" | "perk", id: string) => void;
}) {
  const initial = (biz.name?.[0] ?? "?").toUpperCase();
  return (
    <div className="card card-hover relative" data-testid={`browse-biz-${biz.id}`}>
      {/* Heart sits outside the Link so tapping it doesn't navigate. */}
      <div className="absolute top-3 right-3 z-10">
        <FavoriteButton
          active={favorited}
          onClick={() => onToggleFav("business", biz.id)}
          label={favorited ? "Unfavorite business" : "Favorite business"}
          testid={`fav-biz-${biz.id}`}
        />
      </div>

      <Link
        to={`/brief/${biz.shortCode || biz.id}`}
        className="block no-underline text-inherit hover:no-underline"
        data-testid={`browse-biz-open-${biz.id}`}
      >
        <div className="flex items-start gap-3 pr-10">
          <div className="shrink-0 h-12 w-12 rounded-lg border border-border bg-paper overflow-hidden flex items-center justify-center">
            {biz.logoUrl ? (
              <img
                src={biz.logoUrl}
                alt=""
                className="h-full w-full object-cover"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <span className="text-lg font-semibold text-muted">{initial}</span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="text-base font-semibold truncate">{biz.name}</h4>
            <p className="text-xs text-muted">
              {[biz.city, `${biz.distanceKm} km`].filter(Boolean).join(" · ")}
            </p>
          </div>
        </div>

        {biz.description && (
          <p className="mt-3 text-sm text-muted leading-relaxed line-clamp-2">{biz.description}</p>
        )}
      </Link>

      {biz.perks.length > 0 ? (
        <div className="mt-4 border-t border-border pt-3 grid gap-2">
          {biz.perks.map((p) => (
            <PerkRow
              key={p.id}
              perk={p}
              favorited={favoritedPerks.has(p.id)}
              onToggleFav={() => onToggleFav("perk", p.id)}
            />
          ))}
        </div>
      ) : biz.briefPublished ? (
        <div className="mt-4 border-t border-border pt-3 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-muted">
            No perks right now — reach out from their creator brief.
          </p>
          <Link
            to={`/brief/${biz.shortCode || biz.id}`}
            className="btn text-xs"
            data-testid={`brief-link-${biz.id}`}
          >
            View brief →
          </Link>
        </div>
      ) : (
        <div className="mt-4 border-t border-border pt-3">
          <p className="text-xs text-muted">No active perks or brief yet.</p>
        </div>
      )}
    </div>
  );
}

function PerkRow({
  perk,
  favorited,
  onToggleFav,
}: {
  perk: NearbyBusiness["perks"][number];
  favorited: boolean;
  onToggleFav: () => void;
}) {
  const [claiming, setClaiming] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function claim() {
    setClaiming(true);
    setError(null);
    try {
      await claimPerk(perk.id);
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "couldn't claim");
    } finally {
      setClaiming(false);
    }
  }
  return (
    <div className="rounded-md bg-paper px-3 py-2.5 flex items-start gap-3">
      {perk.imageUrl && (
        <div className="shrink-0 h-12 w-12 rounded-md border border-border bg-surface overflow-hidden">
          <img
            src={perk.imageUrl}
            alt=""
            className="h-full w-full object-cover"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold">{perk.title}</p>
          {perk.requireApproval ? (
            <span className="chip-accent text-[10px] !py-0">Needs approval</span>
          ) : (
            <span className="chip-primary text-[10px] !py-0">Instant</span>
          )}
        </div>
        {perk.description && (
          <p className="mt-0.5 text-xs text-muted line-clamp-2">{perk.description}</p>
        )}
        {error && <p className="mt-1 text-xs text-rose">{error}</p>}
      </div>
      <FavoriteButton
        active={favorited}
        onClick={onToggleFav}
        label={favorited ? "Unfavorite perk" : "Favorite perk"}
        testid={`fav-perk-${perk.id}`}
      />
      <button
        className="btn-primary text-xs shrink-0"
        disabled={claiming || done}
        onClick={claim}
        data-testid={`claim-perk-${perk.id}`}
      >
        {done ? "Claimed" : claiming ? "…" : "Claim"}
      </button>
    </div>
  );
}

function FavoriteButton({
  active,
  onClick,
  label,
  testid,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  testid: string;
}) {
  return (
    <button
      type="button"
      className={`shrink-0 h-8 w-8 rounded-full flex items-center justify-center transition-colors ${
        active
          ? "bg-rose/10 text-rose"
          : "text-muted hover:text-rose hover:bg-rose/5"
      }`}
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      data-testid={testid}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill={active ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
    </button>
  );
}
